import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  LocalizationFlatTeamSettingGroupById,
  type LocalizationFlatTeamSettingGroupId,
} from "./groups";

type LocalizationSettingInput = SettingRegistryMetadataInput;

function setting(metadata: LocalizationSettingInput): LocalizationSettingInput {
  return metadata;
}

function localizationGroup<
  const Group extends LocalizationFlatTeamSettingGroupId,
  const T extends Record<string, LocalizationSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const LocalizationRegistry = {
  ...localizationGroup("localization.general", {
    "localization/enabled": setting({
      label: "Localization enabled",
      description:
        "Whether translated copy is served to shoppers. Feeds the localization provider used by the customer portal and the merchant app.",
      synonyms: ["localization", "translations"],
    }),
  }),
};

export type LocalizationFlatTeamSettingKey = keyof typeof LocalizationRegistry;
const LocalizationFlatTeamSettingKeys = Object.keys(
  LocalizationRegistry,
) as LocalizationFlatTeamSettingKey[];

export const LocalizationFlatTeamSettingCatalogDefinitions =
  LocalizationFlatTeamSettingKeys.map((key) => {
    const definition = LocalizationRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: LocalizationFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: {
        adapter: "teamSettings",
        key,
      } satisfies TeamSettingsSettingValueSource<LocalizationFlatTeamSettingKey>,
    };
  }) satisfies readonly SettingDefinition<
    LocalizationFlatTeamSettingKey,
    LocalizationFlatTeamSettingGroupId
  >[];

export const LocalizationFlatTeamSettingMetadataByKey = Object.fromEntries(
  LocalizationFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<LocalizationFlatTeamSettingKey, SettingMetadata>;
